import type {QueryClient} from "@tanstack/react-query";

import {useMutation, useQueryClient} from "@tanstack/react-query";
import {parseApiListResponse} from "#src/utils/parseApiListResponse";
import {parseApiResponse} from "#src/utils/parseApiResponse";
import {request} from "#src/utils/request";
import {message} from "#src/utils/static-antd";
import {useCallback, useMemo, useState} from "react";

export function invalidateAppEntityListQueries(queryClient: QueryClient, getAllUrl: string): void {
	queryClient.invalidateQueries({
		predicate: q => Array.isArray(q.queryKey) && q.queryKey[0] === getAllUrl,
	});
}

function hasExplicitListSorting(params: Record<string, unknown>): boolean {
	const s = params.sort_by;
	return s !== undefined && s !== null && String(s).trim() !== "";
}

/**
 * When `sort_by` is omitted or blank, sends `sort_by=id` + `sort_order=desc` unless the entity sets
 * `skipDefaultIdDescListSort: true` (no sortable `id` on that list). Any non-blank `sort_by`
 * from the caller wins.
 */
function mergeGetAllSearchParams(
	params: Record<string, unknown> | undefined,
	useDefaultIdDescSort: boolean,
): Record<string, string | number | boolean | undefined> {
	const p: Record<string, unknown> = {...(params ?? {})};

	if (hasExplicitListSorting(p)) {
		return p as Record<string, string | number | boolean | undefined>;
	}

	if (useDefaultIdDescSort) {
		p.sort_by = "id";
		p.sort_order = "desc";
	}

	return p as Record<string, string | number | boolean | undefined>;
}

/** `TRow` / `TWithRelations` tie config at call sites to entity types (see per-entity `*_CRUD_CONFIG`). */
export interface AppEntityCrudConfig<TRow, TWithRelations> {
	__entityTypes?: [TRow, TWithRelations]
	getAllUrl: string
	createUrl: string
	getUpdateUrl: (id: number | string) => string
	getDeleteUrl: (id: number | string) => string
	getByIdUrl: (id: number | string) => string
	getPatchUrl?: (id: number | string, action: string) => string
	getCustomApiPost?: (id: number | string, action: string) => string
	entityPluralName: string
	/** When `true`, do not inject `sort_by=id` + `sort_order=desc` for list GETs (entity has no sortable `id`). */
	skipDefaultIdDescListSort?: boolean
}

export interface AppEntityCrudCore<TRow, TWithRelations> {
	loading: boolean
	error: Error | null
	fetchAll: (params?: Record<string, unknown>) => Promise<{ data: TWithRelations[], total: number }>
	fetchById: (id: number | string) => Promise<TWithRelations | null>
	/** Create new when `id` is absent; update when `id` is present (back-compat name used across pages). */
	createOrEdit: (formData: Partial<TRow>) => Promise<TRow | null>
	create: (formData: Partial<TRow>) => Promise<TRow | null>
	updateById: (formData: Partial<TRow>) => Promise<TRow | null>
	deleteById: (id: number | string) => Promise<unknown | null>
	patchById: (id: number | string, action: string, payload: Record<string, unknown>) => Promise<TRow | null>
	customApiPostById: (id: number | string, action: string, payload?: Record<string, unknown>) => Promise<TRow | null>
}

export function useAppEntityCrud<TRow, TWithRelations>(
	config: AppEntityCrudConfig<TRow, TWithRelations>,
	// initialPageSize = 10,
): AppEntityCrudCore<TRow, TWithRelations> {
	const queryClient = useQueryClient();
	const {getAllUrl, createUrl, getUpdateUrl, getDeleteUrl, getByIdUrl, getPatchUrl, getCustomApiPost, entityPluralName, skipDefaultIdDescListSort} = config;
	const useDefaultIdDescSort = skipDefaultIdDescListSort !== true;

	const [listOpLoading, setListOpLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	const fetchAll = useCallback(
		async (params?: Record<string, unknown>) => {
			setListOpLoading(true);
			setError(null);
			try {
				const searchParams = mergeGetAllSearchParams(params, useDefaultIdDescSort);
				const response = await request
					.get(getAllUrl, {searchParams})
					.json<
					  | ApiListResponse<TWithRelations>
					  | ApiItemsListResponse<TWithRelations>
					  | ApiResponse<ApiListResponse<TWithRelations> | ApiItemsListResponse<TWithRelations>>
					  | TWithRelations[]
					>();
				const parsed = parseApiListResponse(response);
				return parsed;
			} catch (err) {
				setError(err instanceof Error ? err : new Error(`Failed to fetch ${entityPluralName}`));
				console.error(`Fetch ${entityPluralName} error:`, err);
				return {data: [] as TWithRelations[], total: 0};
			} finally {
				setListOpLoading(false);
			}
		},
		[getAllUrl, entityPluralName, useDefaultIdDescSort],
	);

	const createOrUpdateMutation = useMutation({
		mutationFn: async (formData: Partial<TRow>) => {
			const rowId = (formData as { id?: number | string }).id;
			if (!rowId) {
				const response = await request
					.post(createUrl, {json: formData})
					.json<ApiResponse<TRow>>();
				return parseApiResponse(response);
			}
			const response = await request
				.put(getUpdateUrl(rowId), {json: formData})
				.json<ApiResponse<TRow>>();
			return parseApiResponse(response);
		},
		onSuccess: (_data, formData) => {
			const rowId = (formData as { id?: number | string }).id;
			message.success(rowId ? "updated successfully" : "created successfully");
			invalidateAppEntityListQueries(queryClient, getAllUrl);
		},
		onError: (err) => {
			console.error(err);
		},
	});

	const fetchById = useCallback(
		async (id: number | string): Promise<TWithRelations | null> => {
			if (!id)
				return null;

			setListOpLoading(true);
			setError(null);
			try {
				const response = await request
					.get(getByIdUrl(id))
					.json<ApiResponse<TWithRelations>>();
				return parseApiResponse(response);
			} catch (err) {
				setError(err instanceof Error ? err : new Error(`Failed to fetch ${entityPluralName} with id ${id}`));
				console.error(`Fetch ${entityPluralName} by id error:`, err);
				return null;
			} finally {
				setListOpLoading(false);
			}
		},
		[getByIdUrl, entityPluralName],
	);

	const deleteMutation = useMutation({
		mutationFn: async (id: number | string) => {
			const response = await request
				.delete(getDeleteUrl(id))
				.json<ApiResponse<unknown>>();
			return parseApiResponse(response);
		},
		onSuccess: async () => {
			message.success("item deleted successfully");
			invalidateAppEntityListQueries(queryClient, getAllUrl);
			// await fetchAll({page: 1, limit: initialPageSize});
		},
		onError: (err, id) => {
			setError(err instanceof Error ? err : new Error(`Failed to delete ${entityPluralName} with id ${id}`));
			console.error(`Delete ${entityPluralName} error:`, err);
		},
	});

	const createOrUpdate = useCallback(
		async (formData: Partial<TRow>): Promise<TRow | null> => {
			try {
				return await createOrUpdateMutation.mutateAsync(formData);
			} catch {
				return null;
			}
		},
		[createOrUpdateMutation],
	);

	const deleteById = useCallback(
		async (id: number | string): Promise<unknown | null> => {
			if (!id)
				return null;
			setError(null);
			try {
				return await deleteMutation.mutateAsync(id);
			} catch {
				return null;
			}
		},
		[deleteMutation],
	);

	const patchMutation = useMutation({
		mutationFn: async ({id, action, payload}: { id: number | string, action: string, payload: Record<string, unknown> }) => {
			const patchUrl = getPatchUrl?.(id, action) ?? `${getByIdUrl(id)}/${action}`;
			const response = await request
				.patch(patchUrl, {json: payload})
				.json<ApiResponse<TRow>>();
			return parseApiResponse(response);
		},
		onSuccess: () => {
			message.success("updated successfully");
			invalidateAppEntityListQueries(queryClient, getAllUrl);
		},
		onError: (err, {id, action}) => {
			setError(err instanceof Error ? err : new Error(`Failed to patch ${entityPluralName} ${id}/${action}`));
			console.error(`Patch ${entityPluralName} error:`, err);
		},
	});

	const patchById = useCallback(
		async (id: number | string, action: string, payload: Record<string, unknown>): Promise<TRow | null> => {
			if (!id)
				return null;
			setError(null);
			try {
				return await patchMutation.mutateAsync({id, action, payload});
			} catch {
				return null;
			}
		},
		[patchMutation],
	);

	const customApiPostMutation = useMutation({
		mutationFn: async ({id, action, payload}: { id: number | string, action: string, payload: Record<string, unknown> }) => {
			const postUrl = getCustomApiPost?.(id, action) ?? `${getByIdUrl(id)}/${action}`;
			const response = await request
				.post(postUrl, {json: payload})
				.json<ApiResponse<TRow>>();
			return parseApiResponse(response);
		},
		onSuccess: (data) => {
			const successMessage = (data as { message?: string } | null)?.message ?? "posted successfully";
			message.success(successMessage);
			invalidateAppEntityListQueries(queryClient, getAllUrl);
		},
		onError: (err, {id, action}) => {
			setError(err instanceof Error ? err : new Error(`Failed to post ${entityPluralName} ${id}/${action}`));
			console.error(`Custom API post ${entityPluralName} error:`, err);
		},
	});

	const customApiPostById = useCallback(
		async (id: number | string, action: string, payload: Record<string, unknown> = {}): Promise<TRow | null> => {
			if (!id)
				return null;
			setError(null);
			try {
				return await customApiPostMutation.mutateAsync({id, action, payload});
			} catch {
				return null;
			}
		},
		[customApiPostMutation],
	);

	const loading = listOpLoading || createOrUpdateMutation.isPending || deleteMutation.isPending || patchMutation.isPending || customApiPostMutation.isPending;

	return useMemo(
		() => ({
			loading,
			error,
			fetchAll,
			fetchById,
			/** Back-compat alias used by existing pages (`withServerSideCrudPage`). */
			createOrEdit: createOrUpdate,
			create: createOrUpdate,
			updateById: createOrUpdate,
			deleteById,
			patchById,
			customApiPostById,
		}),
		[loading, error, fetchAll, fetchById, createOrUpdate, deleteById, patchById, customApiPostById],
	);
}
export type {AppEntityCrudDefinition} from "#src/hooks/useAppEntityCrud/createAppEntityCrudConfig";
export {createAppEntityCrudConfig} from "#src/hooks/useAppEntityCrud/createAppEntityCrudConfig";
